import { useState, useEffect } from 'react';
import type {
  CourseHearingAttempt,
  CourseHearingDerivedStatus,
  ScheduledCall,
} from '@teachur/http-client';
import {
  getLatestScheduledCall,
  getMissedCutoffTime,
  isScheduledCallMissed,
  type CourseHearingReviewStatus,
} from '../utils/hearingHelpers';

interface UseHearingStatusParams {
  coursePassed: boolean;
  isLocked: boolean;
  scheduledCalls: ScheduledCall[];
  hasJoinedHearing: boolean;
  courseHearingDerivedStatus?: CourseHearingDerivedStatus;
  attempts?: CourseHearingAttempt[] | null;
  attemptsLoaded: boolean;
}

export const useHearingStatus = ({
  coursePassed,
  isLocked,
  scheduledCalls,
  hasJoinedHearing,
  courseHearingDerivedStatus,
  attempts,
  attemptsLoaded,
}: UseHearingStatusParams) => {
  const latestScheduledCall = getLatestScheduledCall(scheduledCalls);
  const scheduledCallStatus = latestScheduledCall?.status;
  const latestAttempt = attempts?.find(
    (a) => a.id === latestScheduledCall?.id || a.videoCallId === latestScheduledCall?.videoCallId
  );

  const [, setTick] = useState(0);

  useEffect(() => {
    if (!latestScheduledCall || latestScheduledCall.status !== 'accepted') return;
    if (attemptsLoaded && latestAttempt?.videoCallEndedAt) return;

    const cutoff = getMissedCutoffTime(latestScheduledCall);
    const delay = cutoff - Date.now();
    if (delay <= 0 || !Number.isFinite(delay)) return;

    const timer = setTimeout(() => setTick((t) => t + 1), delay);
    return () => clearTimeout(timer);
  }, [latestScheduledCall, attemptsLoaded, latestAttempt?.videoCallEndedAt]);

  const isMissed = isScheduledCallMissed(
    latestScheduledCall,
    latestAttempt?.videoCallEndedAt,
    attemptsLoaded
  );

  const isHearingApproved = coursePassed || courseHearingDerivedStatus === 'approved';
  const isNotYet = courseHearingDerivedStatus === 'notYet';
  const hearingStatus: CourseHearingReviewStatus = isHearingApproved
    ? 'approved'
    : isNotYet
      ? 'notYet'
      : undefined;
  const isAfterJoin = hasJoinedHearing || !!hearingStatus;
  const showScheduleMessage = !isHearingApproved && !isAfterJoin && !!scheduledCallStatus;
  // The hearing was resolved without passing (professor said "not yet", or the
  // call was cancelled/rejected), or the accepted slot passed unattended
  // ("missed") → the student may book a new attempt.
  const isRebookable =
    courseHearingDerivedStatus === 'notYet' ||
    courseHearingDerivedStatus === 'cancelled' ||
    isMissed;
  const canScheduleMeeting =
    !isLocked &&
    !isHearingApproved &&
    (scheduledCallStatus === 'rejected' || isRebookable || !latestScheduledCall);

  return {
    latestScheduledCall,
    scheduledCallStatus,
    hearingStatus,
    isMissed,
    showScheduleMessage,
    showPendingScheduleCard: showScheduleMessage && scheduledCallStatus === 'pending',
    showAcceptedScheduleCard:
      showScheduleMessage && scheduledCallStatus === 'accepted' && !isMissed,
    showActiveScheduleCard:
      showScheduleMessage &&
      (scheduledCallStatus === 'accepted' || scheduledCallStatus === 'pending'),
    showCompletedScheduleCard: isHearingApproved && !!latestScheduledCall,
    canScheduleMeeting,
  };
};
